import React, { useMemo } from 'react';
import { DefaultTheme, NavigationContainer, type Theme as NavTheme } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Platform } from 'react-native';
import { type Theme as CacheTheme } from '../styles/theme';
import { useTheme } from '../styles/ThemeProvider';
import SignInScreen from '../screens/auth/SignInScreen';
import SignUpScreen from '../screens/auth/SignUpScreen';
import EmailSignInScreen from '../screens/auth/EmailSignInScreen';
import EmailSignUpScreen from '../screens/auth/EmailSignUpScreen';
import LandingScreen from '../screens/LandingScreen';
import AppraiseScreen from '../screens/AppraiseScreen';
import AppraiseResultScreen from '../screens/AppraiseResultScreen';
import CacheScreen from '../screens/CacheScreen';
import ItemDetailScreen from '../screens/ItemDetailScreen';
import EditExistingItemScreen from '../screens/EditExistingItemScreen';
import ReanalyzeItemScreen from '../screens/ReanalyzeItemScreen';
import PublicShareScreen from '../screens/PublicShareScreen';
import CacheValueExplainerScreen from '../screens/CacheValueExplainerScreen';
import UnifiedBuyFeed from '../screens/buy/UnifiedBuyFeed';
import UnifiedListingScreen from '../screens/buy/UnifiedListingScreen';
import MainNavigator from './MainNavigator';
import { useAuth } from '../context/AuthContext';
import WebNavHeader from '../components/WebNavHeader';
import type { RootStackParamList, CacheStackParamList, BuyStackParamList } from '../types/navigation';

const Stack = createNativeStackNavigator<RootStackParamList>();
const CacheStack = createNativeStackNavigator<CacheStackParamList>();
const BuyStack = createNativeStackNavigator<BuyStackParamList>();

export function CacheNavigator() {
  return (
    <CacheStack.Navigator
      screenOptions={{
        headerShown: Platform.OS !== 'web',
      }}
    >
      <CacheStack.Screen
        name="CacheList"
        component={CacheScreen}
        options={{ headerShown: false }}
      />
      <CacheStack.Screen
        name="ItemDetail"
        component={ItemDetailScreen}
        options={{
          headerShown: false,
          gestureEnabled: true,
          fullScreenGestureEnabled: true,
        }}
      />
      <CacheStack.Screen
        name="EditExistingItem"
        component={EditExistingItemScreen}
        options={{ title: 'Edit Item', headerShown: Platform.OS !== 'web' }}
      />
      <CacheStack.Screen
        name="ReanalyzeItem"
        component={ReanalyzeItemScreen}
        options={{ headerShown: false }}
      />
    </CacheStack.Navigator>
  );
}

// Admin-only marketplace preview. Tab visibility is gated in TabNavigator;
// the screens themselves also check isUserAdmin() defensively. Both surfaces
// are now the single unified Buy feed + listing detail served by the backend
// catalog API (plan catalog-buy-unification A3); the old direct-Supabase
// BuyScreen/ProductScreen and the separate CatalogScreen/ListingScreen pair
// were retired here.
export function BuyNavigator() {
  return (
    <BuyStack.Navigator
      screenOptions={{
        headerShown: false,
      }}
    >
      <BuyStack.Screen name="CatalogList" component={UnifiedBuyFeed} />
      <BuyStack.Screen
        name="CatalogListing"
        component={UnifiedListingScreen}
        options={{ gestureEnabled: true, fullScreenGestureEnabled: true }}
      />
    </BuyStack.Navigator>
  );
}

/**
 * Build a React Navigation theme from a Cache app theme. The nav theme drives
 * default screen backgrounds, header bars, and text on push transitions.
 * Used by ThemedNavigationContainer to keep the React Navigation chrome in
 * sync with the active app theme.
 */
export function createNavTheme(appTheme: CacheTheme): NavTheme {
  return {
    ...DefaultTheme,
    dark: appTheme.mode === 'dark',
    colors: {
      ...DefaultTheme.colors,
      primary: appTheme.colors.accent,
      background: appTheme.colors.backgroundPrimary,
      card: appTheme.colors.backgroundPrimary,
      text: appTheme.colors.textPrimary,
      border: appTheme.colors.border,
      notification: appTheme.colors.destructive,
    },
  };
}

interface ThemedNavigationContainerProps {
  children: React.ReactNode;
  linking?: any;
}

/**
 * NavigationContainer wired to the active app theme. Re-renders the nav
 * theme when the user toggles light/dark or when the OS-level scheme
 * changes (when preference === 'system').
 */
export function ThemedNavigationContainer({ children, linking }: ThemedNavigationContainerProps) {
  const appTheme = useTheme();
  const navTheme = useMemo(() => createNavTheme(appTheme), [appTheme]);
  return (
    <NavigationContainer theme={navTheme} linking={linking}>
      {children}
    </NavigationContainer>
  );
}

export default function RootNavigator() {
  const { session } = useAuth();
  const theme = useTheme();

  return (
    <>
      <WebNavHeader />
      <Stack.Navigator
        screenOptions={{
          headerShown: Platform.OS !== 'web',
          headerStyle: {
            backgroundColor: theme.colors.backgroundPrimary,
          },
          headerTintColor: theme.colors.textPrimary,
        }}
      >
        {session ? (
          <Stack.Screen
            name="Main"
            component={MainNavigator}
            options={{ headerShown: false }}
          />
        ) : (
          <>
            <Stack.Screen
              name="Landing"
              component={LandingScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="SignIn"
              component={SignInScreen}
              options={{ title: 'Sign In', headerBackTitle: 'Back' }}
            />
            <Stack.Screen
              name="SignUp"
              component={SignUpScreen}
              options={{ title: 'Sign Up', headerBackTitle: 'Back' }}
            />
            <Stack.Screen
              name="EmailSignIn"
              component={EmailSignInScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="EmailSignUp"
              component={EmailSignUpScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="Appraise"
              component={AppraiseScreen}
              options={{ headerShown: false }}
            />
            <Stack.Screen
              name="AppraiseResult"
              component={AppraiseResultScreen}
              options={{ headerShown: false }}
            />
          </>
        )}

        <Stack.Screen
          name="PublicShare"
          component={PublicShareScreen}
          options={{ headerShown: false }}
        />
        {/* Outside the session check: guest and signed-in screens both open it. */}
        <Stack.Screen
          name="CacheValueExplainer"
          component={CacheValueExplainerScreen}
          options={{ headerShown: false, presentation: 'modal', title: 'How Cache values your item' }}
        />
      </Stack.Navigator>
    </>
  );
}
